import { deriveAppUrl } from './appUrl';
import { getActiveUid, isImpersonating } from './getActiveUid';
import { clearAuthEmbeddedReturn } from './authEmbedded';

/** Extra signals to hand the destination app. */
export type RedirectOptions = {
  /**
   * Mark this as a brand-new account, so the destination can tell a signup from
   * a login. Set by `OnboardingView` on the post-signup hand-off.
   */
  created?: boolean;
};

export async function redirect({ created = false }: RedirectOptions = {}) {
  const target = getRedirect();

  if (!isValid(target)) throw Error('INVALID_REDIRECT');

  let url = target;

  const destination = new URL(target);
  if (destination.host !== location.host) {
    // Build params via URLSearchParams so values are encoded, separators are
    // correct even when the target already has a query string (e.g. invite
    // links `/?inviteId=…&role=…`), and params land in the query rather than a
    // `#fragment`.
    const activeUid = getActiveUid();

    // Tell the destination which account is active. Identity itself rides the
    // shared `.dabblewriter.com` cookie — this just selects the account.
    // dabble-writer 3.0 reads ?uid=/?impersonate=.
    // `set`, not `append`: the target URL may already carry these params from a
    // previous round-trip (the writer's landing URL becomes the next stored
    // redirect), and append accumulates duplicates (`?uid=x&uid=x&…`).
    if (activeUid) destination.searchParams.set(isImpersonating() ? 'impersonate' : 'uid', activeUid);

    // No credential ever rides the URL (DAB-771). dabble-writer 2.x used to be
    // handed a Firebase custom token here as `?token=`; a query string is not a
    // private channel, and the 3.0 writer's analytics recorded it as the landing
    // page URL of each sign-in. The target can still carry a stale one from an
    // earlier round-trip (see `set` above), so strip it rather than pass it on.
    destination.searchParams.delete('token');

    // A truthy value, not a bare flag: `set('created', '')` serializes as
    // `?created=`, and both writer-side readers (`auth-bootstrap.js`,
    // `accounts.ts`) gate on `if (created)` — the empty string is falsy, so a
    // valueless marker was never seen and every signup was logged as a login.
    if (created) destination.searchParams.set('created', '1');

    url = destination.toString();
  }

  if (sessionStorage.getItem('redirect')) {
    sessionStorage.removeItem('redirect');
  }

  // AuthTemplateBanner promises a setup that this very redirect is what
  // delivers, so the stashed title is consumed with it — left behind, the
  // banner keeps promising it on every later auth screen in the tab.
  sessionStorage.removeItem('template');

  clearAuthEmbeddedReturn();

  window.location.href = url;
}

export function getRedirect() {
  const sessionUrl = sessionStorage.getItem('redirect');
  if (sessionUrl) return sessionUrl;

  // No `?redirect=` was supplied (someone opened /auth, /auth/login or
  // /auth/signup directly, rather than being bounced here by an app). Send them
  // to the writer app — that's what people came to Dabble to use; the billing
  // portal is a destination you navigate to deliberately, not a landing pad.
  // Host-derived so local/test builds stay on their own writer.
  return deriveAppUrl();
}

/**
 * Is this a host we're willing to hand a signed-in user to?
 *
 * A match receives the active account id (`?uid=`/`?impersonate=`), so every
 * rule below is anchored, either to an exact host or to a leading dot
 * (DAB-1255):
 *
 * - `endsWith('dabblewriter.com')` without the dot also accepted
 *   `evildabblewriter.com`, which anyone can register.
 * - `startsWith('localhost')` also accepted `localhost.attacker.example`. The
 *   port lives on the URL, not the hostname, so an exact match loses nothing.
 *
 * `.local` is mDNS (not publicly registrable); it was already dot-anchored.
 *
 * `.dabble-writer.pages.dev` (dabble-writer 2.x) is deliberately NOT allowed
 * (DAB-771). 2.x signs in only from a `?token=` this no longer sends, so landing
 * there tokenless sends it straight back to login — a loop. Rejecting it makes
 * `redirect()` throw `INVALID_REDIRECT`, which every caller lands on the
 * billing portal.
 *
 * Fails closed on an unparseable value: `string` comes from the `?redirect=`
 * URL param (allowlisted into sessionStorage by `lib/sessionData.ts`), and
 * `stores/sidebar.ts` calls this bare inside a getter — a scheme-less value like
 * `?redirect=hello` would otherwise throw `TypeError` out of that computed
 * rather than being rejected.
 */
export function isValid(string: string) {
  let hostname: string;
  try {
    ({ hostname } = new URL(string));
  } catch {
    return false;
  }
  return (
    hostname === 'dabblewriter.com' ||
    hostname.endsWith('.dabblewriter.com') ||
    hostname === 'localhost' ||
    hostname.endsWith('.local')
  );
}
